import { useCallback, useEffect, useRef, useState } from 'react';
import { PhotoIcon } from '@heroicons/react/24/outline';
import { PencilIcon } from '@heroicons/react/20/solid';
import { COMPOSER_ICON_BADGE_CLASS, ComposerIconButton } from '../composer/ComposerIconButton';
import type { AttachmentNotice } from '../composer/useComposerAttachment';
import { ScanMarkupDialog } from './ScanMarkupDialog';
import { useScanMarkupAvailability } from './useScanMarkupAvailability';

interface MarkupScanPhotoButtonProps {
    patientId: string;
    notice: AttachmentNotice | null;
    onAttach: (file: Blob, name: string) => boolean;
    onOpen: () => void;
}

const LABEL = 'Mark up a scan photo';

function MarkupScanPhotoButton({
    patientId,
    notice,
    onAttach,
    onOpen
}: MarkupScanPhotoButtonProps) {
    const [open, setOpen] = useState(false);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const { dmId, availability } = useScanMarkupAvailability(patientId);
    const disabledReason = availability.enabled ? undefined : availability.reason;

    const handleOpen = () => {
        onOpen();
        setOpen(true);
    };

    const close = useCallback(() => setOpen(false), []);

    // After the commit: the button remounts when its tooltip comes back, so an earlier focus is lost.
    const wasOpenRef = useRef(false);
    useEffect(() => {
        if (wasOpenRef.current && !open) buttonRef.current?.focus();
        wasOpenRef.current = open;
    }, [open]);

    return (
        <>
            <ComposerIconButton
                ref={buttonRef}
                label={LABEL}
                active={open}
                disabledReason={disabledReason}
                onClick={handleOpen}
            >
                {/* A photo with a small pen: a scan photo, drawn on. */}
                <span className="relative inline-flex">
                    <PhotoIcon className="w-5 h-5" />
                    <PencilIcon
                        className={`absolute -right-1 -bottom-1 w-3 h-3 ${COMPOSER_ICON_BADGE_CLASS} ${
                            disabledReason ? '' : 'text-red-500'
                        }`}
                    />
                </span>
            </ComposerIconButton>
            {open && (
                <ScanMarkupDialog
                    patientId={patientId}
                    dmId={dmId}
                    notice={notice}
                    onAttach={onAttach}
                    onClose={close}
                />
            )}
        </>
    );
}

export { MarkupScanPhotoButton };
